import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Panel, StatusPill } from "@/components/Peep";

const LABEL = {
  creator_invited: "Creator invited", invite_resent: "Invitation re-sent", creator_revoke: "Invitation revoked",
  invite_accepted: "Invitation accepted", creator_suspend: "Creator suspended", creator_activate: "Creator reactivated",
  went_live: "Went live", ended_live: "Ended live", admin_ended_live: "Broadcast ended by admin",
  password_changed: "Password changed", report_resolved: "Report resolved",
};
const when = (t) => new Date(t).toLocaleString();

function Report({ r, onResolve }) {
  return (
    <div data-testid={`report-row-${r.id}`} className="flex flex-wrap items-start justify-between gap-3 border-t border-white/5 py-3">
      <div>
        <p className="text-sm">{r.reason}</p>
        <p className="text-xs text-[#a7a0af]">{r.session_title} · {r.creator_name || "Unknown creator"} · by @{r.reporter_name} · {when(r.created_at)}</p>
      </div>
      <div className="flex items-center gap-2">
        <StatusPill status={r.status} />
        {r.status === "open" && <button onClick={() => onResolve(r)} data-testid={`report-resolve-${r.id}`} className="flex items-center gap-1 rounded-lg border border-emerald-400/30 px-2 py-1.5 text-xs text-emerald-300"><CheckCircle2 size={12} /> Resolve</button>}
      </div>
    </div>
  );
}

export default function AdminActivity() {
  const [d, setD] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/activity").then((r) => setD(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const resolve = async (r) => {
    try {
      await api.post(`/admin/reports/${r.id}/resolve`);
      toast.success("Report resolved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  if (!d) return <p className="text-sm text-[#a7a0af]">Loading…</p>;
  return (
    <div data-testid="admin-activity" className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl font-extrabold">Activity & moderation</h1>
        <p className="mt-1 text-sm text-[#a7a0af]">Creator account events, broadcasts and viewer reports.</p>
      </div>
      <Panel title={`Moderation reports · ${d.open_reports} open`}>
        {d.reports.length === 0 && <p data-testid="reports-empty" className="text-sm text-[#a7a0af]">No reports yet.</p>}
        {d.reports.map((r) => <Report key={r.id} r={r} onResolve={resolve} />)}
      </Panel>
      <Panel title="Recent activity">
        {d.activity.length === 0 && <p data-testid="activity-empty" className="text-sm text-[#a7a0af]">No activity yet.</p>}
        {d.activity.map((a) => (
          <div key={a.id} data-testid="activity-row" className="flex flex-wrap justify-between gap-2 border-t border-white/5 py-2 text-xs">
            <span><span className="font-semibold text-white">{LABEL[a.action] || a.action}</span> <span className="text-[#a7a0af]">{a.creator_name ? `· ${a.creator_name} ` : ""}{a.detail ? `· ${a.detail}` : ""}</span></span>
            <span className="text-[#6f6878]">@{a.actor_name} · {when(a.created_at)}</span>
          </div>
        ))}
      </Panel>
    </div>
  );
}
